import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { DollarSign, Users, Globe2, FolderKanban } from "lucide-react";

const currency = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function Reports() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/reports/summary").then(({ data }) => setD(data)).catch(() => setD({})); }, []);
  const monthly = d?.monthly_sales || [];
  const max = Math.max(1, ...monthly.map(m => m.revenue));

  return (
    <DashboardLayout title="Reports" subtitle="Business intelligence · revenue, clients, renewals, projects">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="reports-kpis">
        <div className="kpi-card" data-testid="reports-kpi-revenue">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Revenue (6 mo)</div>
            <div className="w-8 h-8 grid place-items-center rounded-md bg-emerald-500/10 text-emerald-400"><DollarSign className="w-4 h-4" /></div>
          </div>
          <div className="stat-num text-zinc-100">{currency(monthly.reduce((s, m) => s + m.revenue, 0))}</div>
        </div>
        <div className="kpi-card" data-testid="reports-kpi-clients">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Active clients</div>
            <div className="w-8 h-8 grid place-items-center rounded-md bg-blue-500/10 text-blue-400"><Users className="w-4 h-4" /></div>
          </div>
          <div className="stat-num text-zinc-100">{d?.active_clients ?? "—"}</div>
        </div>
        <div className="kpi-card" data-testid="reports-kpi-domains">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Expiring domains</div>
            <div className="w-8 h-8 grid place-items-center rounded-md bg-amber-500/10 text-amber-400"><Globe2 className="w-4 h-4" /></div>
          </div>
          <div className="stat-num text-zinc-100">{d?.expiring_domains ?? "—"}</div>
        </div>
        <div className="kpi-card" data-testid="reports-kpi-projects">
          <div className="flex items-center justify-between mb-4">
            <div className="overline">Projects</div>
            <div className="w-8 h-8 grid place-items-center rounded-md bg-violet-500/10 text-violet-400"><FolderKanban className="w-4 h-4" /></div>
          </div>
          <div className="stat-num text-zinc-100">{(d?.projects_status || []).reduce((s, p) => s + p.count, 0)}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="reports-monthly">
          <h3 className="font-display text-sm font-semibold text-zinc-100 mb-4">Monthly sales</h3>
          <div className="h-56 flex items-end gap-2">
            {monthly.map((m) => (
              <div key={m.month} className="flex-1 flex flex-col items-center gap-2">
                <div className="w-full rounded-sm bg-gradient-to-t from-blue-500/80 to-cyan-400/80" style={{ height: `${(m.revenue / max) * 100}%` }} title={`$${m.revenue.toFixed(2)}`} />
                <div className="text-[10px] text-zinc-500">{m.month.slice(5)}</div>
              </div>
            ))}
            {monthly.length === 0 && <div className="w-full text-center text-xs text-zinc-500">No data</div>}
          </div>
        </div>

        <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="reports-projects-status">
          <h3 className="font-display text-sm font-semibold text-zinc-100 mb-4">Project status</h3>
          <ul className="space-y-2 text-sm">
            {(d?.projects_status || []).map((r) => (
              <li key={r._id} className="flex items-center justify-between">
                <span className="text-zinc-300">{r._id || "unknown"}</span>
                <span className="text-blue-400">{r.count}</span>
              </li>
            ))}
            {(!d?.projects_status || d.projects_status.length === 0) && <li className="text-xs text-zinc-500">No data</li>}
          </ul>
        </div>

        <div className="lg:col-span-3 rounded-xl border border-zinc-800 bg-zinc-900/40 p-6" data-testid="reports-expiring">
          <h3 className="font-display text-sm font-semibold text-zinc-100 mb-4">Domains expiring soon</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(d?.expiring_domains_list || []).map((it) => (
              <div key={it.record_id} className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-sm text-zinc-100">{(it.data || {}).name || (it.data || {}).domain || "—"}</div>
                <div className="text-xs text-zinc-500 mt-1">Expires {(it.data || {}).expires_at?.slice(0, 10) || "—"}</div>
              </div>
            ))}
            {(!d?.expiring_domains_list || d.expiring_domains_list.length === 0) && (
              <div className="col-span-full text-xs text-zinc-500">No expiring domains in the next 30 days.</div>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
